import {
  AddPageEvent,
  AddSectionEvent,
  AnalyticsService,
} from 'base/analytics';
import { exists } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { PublicComponent } from 'base/library_service';
import { Button } from 'base/ui/button';
import { EMPTY_IMAGE_SRC } from 'base/ui/empty_image';
import { IconChecked } from 'base/ui/icons';
import { Inline, Rows } from 'base/ui/layout';
import { TextSmall, TextTinyLight } from 'base/ui/typography';
import classNames from 'classnames';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { computedFn } from 'mobx-utils';
import React from 'react';
import { Assistant } from '../ai/assistant';
import { makeReplaceComponentSuggestion } from '../ai/component_content_mapping';
import { getGenerateCopyError } from '../ai/validation';
import { anElementInstance, anInlineSection, aPage, aSection } from '../model';
import { findParentSpace, parentsMap } from '../model_helpers';
import { SelectionStore } from '../selection/store';
import { ProjectSession } from '../session/session';
import { pageDndDomain } from '../sitemap/page_dnd';
import { sectionDndDomain, SectionPosition } from '../sitemap/section_dnd';
import { commitSuggestion, Suggestion } from '../suggested_edits';
import styles from './component_results.css';
import { PageTemplate, SectionCategory, stripTrailingNumber } from './content';
import { RemoveSingleSuggestion, SingleSuggestionStore } from './suggestions';

export type ComponentResultProps = {
  component: PublicComponent;
  category?: SectionCategory;
  interaction: 'add' | 'replace';
  onUsed?: () => void;
  analyticsContext: Pick<AddSectionEvent, 'search' | 'categoryName'>;
};

export type PageTemplateResultProps = {
  pageTemplate: PageTemplate;
  analyticsContext: Pick<AddPageEvent, 'search' | 'categoryName'>;
};

export function useResultComponents(
  selection: SelectionStore,
  session: ProjectSession,
  library: LibraryCache,
  assistant: Assistant,
  analytics: AnalyticsService,
  suggestionStore: SingleSuggestionStore,
) {
  // clear suggestion on unmount
  React.useEffect(() => {
    return () => {
      suggestionStore.clear();
    };
  }, [suggestionStore]);

  const deps = React.useMemo(
    () => ({
      selection,
      session,
      library,
      assistant,
      suggestionStore,
      analytics,
    }),
    [selection, session, library, assistant, suggestionStore, analytics],
  );

  return {
    ComponentResult: React.useCallback(
      (props: ComponentResultProps) => (
        <ComponentResultImpl {...props} {...deps} />
      ),
      [deps],
    ),
    PageTemplateResult: React.useCallback(
      (props: PageTemplateResultProps) => (
        <PageTemplateResultImpl {...props} {...deps} />
      ),
      [deps],
    ),
  };
}

type Deps = {
  selection: SelectionStore;
  session: ProjectSession;
  library: LibraryCache;
  assistant: Assistant;
  suggestionStore: SingleSuggestionStore;
  analytics: AnalyticsService;
};

const getSelectedPage = computedFn((store: SelectionStore) => {
  const sel = store.get();
  return (
    sel.page ||
    (sel.space !== 'home' && sel.space?.subPages[0]) ||
    store.project.homePage
  );
});

const getSelectedSection = computedFn((store: SelectionStore) => {
  const sel = store.get();
  return sel.section
    ? sel.section.type === 'inline'
      ? sel.section.value
      : store.project.globalSections.get(sel.section.id)
    : undefined;
});

class ComponentResultModel {
  constructor(
    private component: PublicComponent,
    private category: SectionCategory | undefined,
    private interaction: 'add' | 'replace',
    private selection: SelectionStore,
    private library: LibraryCache,
  ) {
    makeObservable(this);
  }

  get page() {
    return getSelectedPage(this.selection);
  }

  get selectedSection() {
    return getSelectedSection(this.selection);
  }

  @computed.struct
  get newSection() {
    const { component, category } = this;
    return aSection({
      name: category?.name || stripTrailingNumber(component.name),
      description: category?.description || '',
      element: {
        current: anElementInstance({
          source: { slug: component.componentSlug },
        }),
      },
    });
  }

  @computed
  get suggestion() {
    const {
      page,
      selectedSection,
      newSection,
      component,
      category,
      interaction,
      library,
    } = this;
    const sel = this.selection.get();

    if (sel.section && selectedSection && interaction === 'replace') {
      return {
        ...makeReplaceComponentSuggestion(
          selectedSection,
          component.componentSlug,
          library,
        ),
        page,
        sref: sel.section,
        section: selectedSection,
        defaultName: newSection.name,
        defaultDescription: category?.description || '',
      } satisfies Suggestion;
    } else {
      return {
        type: 'add-sections',
        page,
        index: sel.section
          ? page.sections.indexOf(sel.section) + 1
          : page.sections.length,
        sections: [anInlineSection(newSection)],
      } satisfies Suggestion;
    }
  }

  @computed
  get draggableSource() {
    return { section: anInlineSection(this.newSection) };
  }

  @computed
  get isSelected() {
    if (this.interaction !== 'replace') return false;
    const source = getSelectedSection(this.selection)?.element?.current?.source;
    if (!source) return false;
    return source.slug === this.component.componentSlug;
  }
}

const ComponentResultImpl = observer(function _ComponentResultImpl({
  component,
  category,
  analyticsContext,
  selection,
  session,
  assistant,
  suggestionStore,
  interaction,
  onUsed,
  library,
  analytics,
}: ComponentResultProps & Deps) {
  const removeMySuggestion = React.useRef<RemoveSingleSuggestion>();

  const model = React.useMemo(
    () =>
      new ComponentResultModel(
        component,
        category,
        interaction,
        selection,
        library,
      ),
    [component, category, interaction, selection, library],
  );

  const trackAdded = () => {
    const previousComponentSource =
      model.selectedSection?.element?.current?.source;
    analytics.track({
      event: 'blocks/add-section',
      method: 'result',
      name: model.newSection.name,
      previousComponentSlug: previousComponentSource?.slug,
      componentSlug: component.componentSlug,
      interaction,
      ...analyticsContext,
    });
  };
  const onAfterDrop = (dest: SectionPosition) => {
    trackAdded();
    // For newly added sections, we autogenerate copy if we have the required
    // fields to do so.
    const sref = dest.page.sections[dest.index];
    const space = findParentSpace(selection.project, dest.page) || 'home';
    const error = getGenerateCopyError(
      sref,
      dest.page.state,
      space,
      selection.project.state,
    );
    if (!error) {
      assistant.generateCopy(new Map([[sref, undefined]]));
    }
  };
  const handleClick = () => {
    suggestionStore.clear();
    const { selectedSection, suggestion } = model;
    if (model.isSelected && selectedSection) {
      selectedSection.element.replace(undefined);
      selectedSection.isComponentOutOfSync = false;
      return;
    }
    if (!session.canViewWireframe(suggestion.page)) {
      session.showUpgradeWall?.('pages');
      return;
    }
    trackAdded();
    onUsed?.();
    if (suggestion.type === 'replace-component') {
      const oldInstance = suggestion.section.element.current?.state;
      commitSuggestion(suggestion, selection, library);
      if (suggestion.needsToGenerateCopy) {
        assistant.generateCopy(new Map([[suggestion.sref, oldInstance]]));
      }
      return;
    }
    commitSuggestion(suggestion, selection, library);

    // New section, auto generate copy if we can.
    const sref = suggestion.page.sections[suggestion.index];
    const space = findParentSpace(selection.project, suggestion.page) || 'home';
    const error = getGenerateCopyError(
      sref,
      suggestion.page.state,
      space,
      selection.project.state,
    );
    if (!error) assistant.generateCopy(new Map([[sref, undefined]]));
  };
  const handleMouseEnter = () => {
    if (sectionDndDomain.state.get()) return;
    removeMySuggestion.current = suggestionStore.set(() => model.suggestion);
  };
  const handleMouseLeave = () => {
    // a delay to reduce flickering when mousing between buttons
    removeMySuggestion.current?.({ delayMs: 100 });
  };
  const handleDragStart = () => {
    removeMySuggestion.current?.();
  };
  React.useEffect(() => () => removeMySuggestion.current?.(), []);

  const handleThumbnailSettled = React.useCallback(() => {
    // Once we've loaded the image, we should start loading the real thing
    // to reduce latency if the user uses this component.
    // (AWS CloudFront usually gives us the component json in <10ms, but
    //  if it isn't in that region's cache, it can take 800ms+).
    library.getCachedPublicComponentJson(component.componentSlug, {
      fetchIfMissing: true,
    });
  }, [component, library]);

  return (
    <Card
      isSelected={model.isSelected}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <sectionDndDomain.Draggable
        source={model.draggableSource}
        onDragStart={handleDragStart}
        dragPreviewClass={styles.dragPreview}
        dragPreviewHoveringClass={styles.dragPreviewIsHovering}
        onAfterDrop={(_, dest: SectionPosition) => onAfterDrop(dest)}
      >
        <ComponentResultContent
          {...component}
          onThumbnailSettled={handleThumbnailSettled}
          isSelected={model.isSelected}
        />
      </sectionDndDomain.Draggable>
    </Card>
  );
});

const PageTemplateResultImpl = observer(function _PageTemplateResultImpl({
  pageTemplate,
  analyticsContext,
  selection,
  suggestionStore,
  library,
  analytics,
}: PageTemplateResultProps & Deps) {
  const removeMySuggestion = React.useRef<RemoveSingleSuggestion>();

  const page = getSelectedPage(selection);
  const parent = parentsMap(selection.project).get(page);

  const newPageState = aPage({
    name: stripTrailingNumber(pageTemplate.name),
    sections: pageTemplate.sections.map(anInlineSection),
  });
  const suggestion: Suggestion =
    page.sections.length === 0
      ? {
          type: 'add-sections',
          page,
          index: 0,
          sections: pageTemplate.sections.map(anInlineSection),
          replaceEmptyPageName: newPageState.name,
        }
      : {
          type: 'add-page',
          page: newPageState,
          parent: parent || page,
          index: parent ? parent.subPages.indexOf(page) + 1 : 0,
        };

  const trackAdded = () => {
    analytics.track({
      event: 'blocks/add-page',
      method: 'result',
      name: page.name,
      componentSlugs: pageTemplate.sections
        .map((c) => c.element.current?.source.slug)
        .filter(exists),
      ...analyticsContext,
    });
  };
  const handleClick = () => {
    suggestionStore.clear();
    commitSuggestion(suggestion, selection, library);
    trackAdded();
  };
  const handleMouseEnter = () => {
    if (sectionDndDomain.state.get()) return;
    removeMySuggestion.current = suggestionStore.set(suggestion);
  };
  const handleMouseLeave = () => {
    // a delay to reduce flickering when mousing between buttons
    removeMySuggestion.current?.({ delayMs: 100 });
  };
  const handleDragStart = () => {
    removeMySuggestion.current?.();
  };
  React.useEffect(() => () => removeMySuggestion.current?.(), []);

  const handleThumbnailSettled = React.useCallback(() => {
    // Once we've loaded the image, we should start loading the real thing
    // to reduce latency if the user uses this component.
    // (AWS CloudFront usually gives us the component json in <10ms, but
    //  if it isn't in that region's cache, it can take 800ms+).
    for (const section of pageTemplate.sections.slice(0, 3)) {
      const source = section.element.current?.source;
      if (!source) continue;
      library.getCachedPublicComponentJson(source.slug, {
        fetchIfMissing: true,
      });
    }
  }, [pageTemplate, library]);

  return (
    <Card
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <pageDndDomain.Draggable
        source={{ page: newPageState }}
        onDragStart={handleDragStart}
        dragPreviewClass={styles.dragPreview}
        dragPreviewHoveringClass={styles.dragPreviewIsHovering}
        onAfterDrop={trackAdded}
      >
        <ComponentResultContent
          {...pageTemplate}
          onThumbnailSettled={handleThumbnailSettled}
          thumbnailUrl={pageTemplate.thumbnailUrl}
          thumbnailWidth={7}
          thumbnailHeight={6}
          isSelected={false}
        />
      </pageDndDomain.Draggable>
    </Card>
  );
});

function ComponentResultContent({
  thumbnailUrl,
  thumbnailWidth,
  thumbnailHeight,
  onThumbnailSettled,
  name,
  isSelected,
}: {
  thumbnailUrl: string | undefined;
  thumbnailWidth: number;
  thumbnailHeight: number;
  onThumbnailSettled: () => void;
  name: string;
  isSelected: boolean;
}) {
  const imageRef = React.useRef<HTMLImageElement>(null);
  React.useEffect(() => {
    if (imageRef.current && imageRef.current.complete) {
      onThumbnailSettled();
    }
  }, [onThumbnailSettled]);

  return (
    <>
      <div className={styles.componentName}>
        <Inline spacing={4} xalign="space-between" yalign="center">
          <TextTinyLight lineClamp={1}>{name}</TextTinyLight>
          {isSelected && (
            <div className={styles.selectedIcon} aria-label="Selected">
              <IconChecked />
            </div>
          )}
        </Inline>
      </div>
      <img
        ref={imageRef}
        className={styles.componentImage}
        src={thumbnailUrl || EMPTY_IMAGE_SRC}
        style={{ aspectRatio: `${thumbnailWidth}/${thumbnailHeight}` }}
        draggable={false}
        onLoad={onThumbnailSettled}
        onError={onThumbnailSettled}
      />
    </>
  );
}

export function LoadingResults() {
  return (
    <Rows spacing={8}>
      {Array.from({ length: 10 }).map((_, i) => (
        <LoadingResult key={i} index={i} />
      ))}
    </Rows>
  );
}

export function LoadingResult({ index }: { index: number }) {
  return (
    <div
      className={styles.resultPlaceholder}
      style={{
        animationDelay: `${index * 0.1}s`,
        aspectRatio: `4 / ${1 + index * 0.25}`,
      }}
    />
  );
}

export function ErrorState({ error }: { error: unknown }) {
  return <TextSmall>Error loading library: {`${error}`}</TextSmall>;
}

function Card({
  isSelected,
  onClick,
  onMouseEnter,
  onMouseLeave,
  children,
}: {
  isSelected?: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      className={classNames(styles.card, isSelected && styles.isSelected)}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
      <div className={styles.ring} />
    </button>
  );
}

export function SearchNoResults({
  clearSearch,
  subject,
}: {
  clearSearch: () => void;
  subject: 'section' | 'page';
}) {
  return (
    <Rows spacing={16} xalign="center">
      <div />
      <Rows spacing={8} xalign="center">
        <TextSmall>No sections found</TextSmall>
        <TextTinyLight color="grey">
          Try searching for a similar {subject} name.
        </TextTinyLight>
      </Rows>
      <Button onClick={clearSearch} label="Clear search" />
      <div />
    </Rows>
  );
}
